import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { PatientStatus } from "@domain/patient/patient";
import {
	AnalyticsProgramClickType,
	AnalyticsProgramLocation,
	sendAnalyticsEventViewPatientDetail,
} from "@domain/program/program-analytics";
import { isGlobalProgramStatusExtendable } from "@domain/program/program-status/program-status-utils";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { ConditionalSkeleton, ConditionalSkeletonProps } from "@ui/components/common/conditional-skeleton";
import { ProgramStatusComponent } from "@ui/components/program/program-status-component";
import { RoutesPath, TabPatientDetails } from "@ui/navigation/routes";
import { PatientActionButton } from "@ui/pages/home/components/patient-action-button";
import { ExtendedRegisteredPatient } from "@ui/pages/home/components/registered-patient-listing";
import { usePatientListingItem } from "@ui/pages/home/hooks/use-patient-listing-item";
import { useProgramActions } from "@ui/pages/program-builder/hooks/use-program-actions";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import React from "react";

interface PatientRowProps {
	patient: ExtendedRegisteredPatient;
	complianceSkeletonProps: ConditionalSkeletonProps;
}

export const RegisteredPatientListingRowMobile: React.FC<PatientRowProps> = ({ patient, complianceSkeletonProps }) => {
	const { id, disease, name, analyticsId, compliance, lastCompletedSessionDate, globalProgramStatus } = patient;
	const { format, formatPercent } = useI18n();
	const navigate = useNavigate();
	const { getFormattedLastSessionDate } = usePatientListingItem();

	const { extendProgram } = useProgramActions();

	const emptyWording = format("global.empty");

	return (
		<FlexContainer
			onClick={() => {
				navigate(RoutesPath.PatientDetails, {
					route: { patientId: id },
					query: { active: TabPatientDetails.PROGRESS_TAB },
				});
				sendAnalyticsEventViewPatientDetail(analyticsId, AnalyticsProgramClickType.LIST_CLICK);
			}}
		>
			<Content>
				<KebabContainer>
					<PatientActionButton patient={{ ...patient, status: PatientStatus.Registered }} />
				</KebabContainer>
				<PatientInformation>
					<PatientNameContainer>
						<PatientName className="p-size-s-bold">{name}</PatientName>
						<Disease className="p-size-xs-semi-bold">{disease}</Disease>
					</PatientNameContainer>
					<ProgramStatusComponent programStatus={patient.globalProgramStatus} />
					<Row>
						<ConditionalSkeleton {...complianceSkeletonProps}>
							<div>{format("home.patientsListing.globalComplianceRowMobile")}</div>
							<div className="p-size-s-bold">{isNotNil(compliance) ? formatPercent(compliance) : emptyWording}</div>
						</ConditionalSkeleton>
					</Row>
					<Row>
						<div>{format("home.patientsListing.lastSessionCompletedRowMobile")}</div>
						<div className="p-size-s-bold">
							{isNotNil(lastCompletedSessionDate)
								? getFormattedLastSessionDate(lastCompletedSessionDate)
								: emptyWording}
						</div>
					</Row>
					{isGlobalProgramStatusExtendable(globalProgramStatus) && (
						<ExtendButton
							label={format("home.registeredPatientsListing.extendColumn.button")}
							onClick={event => {
								event.stopPropagation();
								extendProgram(
									AnalyticsProgramLocation.HOME,
									patient.id,
									patient.analyticsId,
									globalProgramStatus.programId,
									globalProgramStatus.type,
								);
							}}
							uiStyle="secondary"
						/>
					)}
				</PatientInformation>
			</Content>
		</FlexContainer>
	);
};

const FlexContainer = styled.div`
	${row(undefined, "spacebetween", "center")};
	padding: 24px 32px 24px 24px;
	border-bottom: 1px solid ${colors.grey25};
	cursor: pointer;
	position: relative;
`;

const Content = styled.div`
	overflow: hidden;
	width: 100%;
`;

const PatientInformation = styled.div`
	${stack(8)}
`;

const PatientNameContainer = styled.div`
	${stack()}
	overflow: hidden;
	align-self: stretch;
	text-overflow: ellipsis;
	margin-bottom: 10px;
`;
const PatientName = styled.p`
	overflow: hidden;
	align-self: stretch;
	text-overflow: ellipsis;
`;
const KebabContainer = styled.div`
	position: absolute;
	right: 8px;
	top: 8px;
`;

const Row = styled.div`
	${row(8)}
`;

const Disease = styled.span`
	color: ${colors.grey} !important;
	margin-top: 4px;
`;

const ExtendButton = styled(Button)`
	margin-top: 8px;
`;
